import {
  useGetSellerCustomers,
  useGetSellerCustomersInfinite,
} from '../../../api/lending/lapiHooks';
import { CustomerRow, TabConfig } from '../CustomersV2.types';
import { useCustomersTableState } from './useCustomersTableState';
import { TableSettings } from './useTableSettings';

/** Stable identity, so a consumer holding it in a dependency array doesn't churn. */
const NO_NEXT_PAGE = () => {};

/**
 * Rows for the customers table.
 *
 * Two fetching strategies, one per layout: the desktop table pages through the
 * result set a page at a time, while the mobile layout accumulates pages as the
 * seller scrolls. Both hooks are called on every render — hooks can't be
 * conditional — so the one that isn't driving the current layout is disabled and
 * fetches nothing.
 *
 * The mobile branch ignores `state.page` entirely: its cursor lives in React
 * Query, not the URL. `setPage` is still returned for the desktop pagination.
 */
export function useCustomersQuery(
  config: TabConfig,
  settings: TableSettings,
  { infinite = false }: { infinite?: boolean } = {},
) {
  const { state, setPage, setFilter, setSort } = useCustomersTableState(
    config,
    settings,
  );

  const filterParams =
    config.filterParam === 'application_stage'
      ? { application_stage: state.filterValue ?? undefined }
      : { spending_limit_status: state.filterValue ?? undefined };

  const sharedParams = {
    view: config.view,
    ordering: state.sort,
    search: state.search ?? undefined,
    include_latest_payment: settings.latestPayment || undefined,
    include_declined: settings.declined || undefined,
    ...filterParams,
  };

  const paged = useGetSellerCustomers(
    { page: state.page, ...sharedParams },
    { enabled: !infinite },
  );

  const accumulated = useGetSellerCustomersInfinite(sharedParams, {
    enabled: infinite,
  });

  const active = infinite ? accumulated : paged;

  return {
    rows: (active.sellerCustomers ?? []) as CustomerRow[],
    meta: active.metadata,
    loading: active.loadingSellerCustomers,
    state,
    setPage,
    setFilter,
    setSort,
    // Gated on `infinite` rather than passed straight through: `fetchNextPage` is
    // imperative and would fetch even though the query behind it is disabled,
    // seeding a cache entry that makes `hasNextPage` true on desktop too.
    fetchNextPage: infinite ? accumulated.fetchNextPage : NO_NEXT_PAGE,
    hasNextPage: infinite && accumulated.hasNextPage,
    isFetchingNextPage: infinite && accumulated.isFetchingNextPage,
  };
}
